import { Injectable } from '@angular/core';
import { ICellRendererParams, ValueGetterParams } from '@ag-grid-community/core';

import { CommonGridOptions } from '../../../shared/datacenter/grid-options';
import {
	CommonColDef,
	DateColDef,
	LinkColDef,
	NumericColDef
} from '../../../shared/datacenter/column-defs';
import { TranslationService } from '../../../shared/services/translation.service';
import { Batch, OrderBatch } from '../../models/batch';
import {
	CalledBatch,
	EcallOffCardItemResponse
} from '../../../ecall-off/models/ecall-off-model';

const linkToBatchCardCellRendererParams = {
	routerLink(params: ICellRendererParams<OrderBatch | Batch, unknown>) {
		const batch = params.data;
		if (!batch) {
			return undefined;
		}
		const batchId = (<Batch> batch).id;
		if (!batchId) {
			return undefined;
		}
		return {
			commands: [
				'/order-tracking',
				'batch',
				'card',
				batchId
			]
		};
	}
};

const linkToBatchCardCellCalledBatchesRendererParams = {
	routerLink(params: ICellRendererParams<CalledBatch, unknown>) {
		const batch = params.data;
		if (!batch) {
			return undefined;
		}
		return {
			commands: [
				'/order-tracking',
				'batch',
				'card',
				batch.supplierBatchReference
			]
		};
	}
};

@Injectable({ providedIn: 'root' })
export class OrderCardBatchGridBuilderService {
	constructor(private readonly translationService: TranslationService) {}

	buildBlockedBatchesGrid(batches: OrderBatch[]): CommonGridOptions<OrderBatch> {
		return new CommonGridOptions<OrderBatch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.detailed-batch-status'),
					field: 'batchStatusDescription'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.current-location'),
					field: 'currentLocationDescription'
				}),
			]
		});
	}

	buildReadyToShipGrid(batches: Batch[]): CommonGridOptions<Batch> {
		return this.buildReadyToShipOrCalledGrid(batches);
	}

	buildCalledBatchesGrid(batches: Batch[]): CommonGridOptions<Batch> {
		return this.buildReadyToShipOrCalledGrid(batches);
	}

	buildAtReshipperGrid(batches: OrderBatch[]): CommonGridOptions<OrderBatch> {
		return new CommonGridOptions<OrderBatch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.detailed-batch-status'),
					field: 'batchStatusDescription'
				}),
				new DateColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.date-for-dispatch'),
					field: 'readyToShipDate'
				}),
				new DateColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.planned-loading-date'),
					field: 'plannedShipmentDate'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.current-location'),
					field: 'currentLocationDescription'
				}),
			]
		});
	}

	buildDeliveredGrid(batches: OrderBatch[]): CommonGridOptions<OrderBatch> {
		return new CommonGridOptions<OrderBatch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellCalledBatchesRendererParams
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new DateColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.date-for-dispatch'),
					field: 'readyToShipDate'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.transport-reference'),
					valueGetter: params => this.getTransportReference(params)
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('order.transport-mode'),
					valueGetter: params => this.getTransportModeDescription(params)
				}),
				new DateColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-date'),
					field: 'dispatchAdviceDate'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-advice'),
					field: 'dispatchAdviceNumber'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.to'),
					field: 'destinationDescription'
				}),
			]
		});
	}

	buildLandVesselGrid(batches: OrderBatch[]): CommonGridOptions<OrderBatch> {
		return new CommonGridOptions<OrderBatch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.detailed-batch-status'),
					field: 'batchStatusDescription'
				}),
				new DateColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.date-for-dispatch'),
					field: 'readyToShipDate'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.transport-reference'),
					valueGetter: params => this.getTransportReference(params)
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('order.transport-mode'),
					valueGetter: params => this.getTransportModeDescription(params)
				}),
				new DateColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-date'),
					field: 'dispatchAdviceDate'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-advice'),
					field: 'dispatchAdviceNumber'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.to'),
					field: 'destinationDescription'
				}),
			]
		});
	}

	buildConsignmentOverseasGrid(batches: OrderBatch[]): CommonGridOptions<OrderBatch> {
		return new CommonGridOptions<OrderBatch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.detailed-batch-status'),
					field: 'batchStatusDescription'
				}),
				new DateColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.date-for-dispatch'),
					field: 'readyToShipDate'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('batch.current-location'),
					field: 'currentLocationDescription'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.transport-reference'),
					valueGetter: params => this.getTransportReference(params)
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('order.transport-mode'),
					valueGetter: params => this.getTransportModeDescription(params)
				}),
				new DateColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-date'),
					field: 'dispatchAdviceDate'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.dispatch-advice'),
					field: 'dispatchAdviceNumber'
				}),
				new CommonColDef<OrderBatch>({
					headerName: this.translationService.getTR('shipment.to'),
					field: 'destinationDescription'
				}),
			]
		});
	}

	buildEcallBatchesGrid(currentSupplierOrderNumber?: string, currentSupplierOrderItemNumber?: string) {
		return new CommonGridOptions({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef({
					headerName: this.translationService.getTR('commercial-documents.batch-number'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellCalledBatchesRendererParams
				}),
				new LinkColDef({
					headerName: this.translationService.getTR('order.supplier.order.number'),
					valueGetter: params => {
						if (!params.data) {
							return undefined;
						}
						const item = <EcallOffCardItemResponse> params.data;
						if (!item.supplierOrderNumber) {
							return undefined;
						}
						// Format as supplierOrderNumber/supplierOrderItemNumber
						return item.supplierOrderItemNumber
							? `${ item.supplierOrderNumber }/${ item.supplierOrderItemNumber }`
							: item.supplierOrderNumber;
					},
					cellRendererParams: {
						routerLink: (params: ICellRendererParams<EcallOffCardItemResponse, unknown>) => {
							const item = params.data;
							if (!item?.supplierOrderNumber) {
								return undefined;
							}
							if (item.supplierOrderNumber === currentSupplierOrderNumber
								&& item.supplierOrderItemNumber === currentSupplierOrderItemNumber) {
								return undefined;
							}
							return {
								commands: [
									'/order-tracking',
									'order',
									'card',
									item.supplierOrderNumber,
									item.supplierOrderItemNumber || ''
								]
							};
						}
					},
					cellStyle: params => {
						const item = <EcallOffCardItemResponse | undefined> params.data;
						if (!item?.supplierOrderNumber) {
							return undefined;
						}
						return item.supplierOrderNumber !== currentSupplierOrderNumber
							|| item.supplierOrderItemNumber !== currentSupplierOrderItemNumber
							? { fontWeight: 'bold' }
							: undefined;
					}
				}),
				new NumericColDef({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchBrut',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef({
					headerName: this.translationService.getTR('ecall-off.unloading-reference'),
					field: 'unloadingReference'
				}),
				new CommonColDef({
					headerName: this.translationService.getTR('ecall-off.exception'),
					field: 'comment'
				}),
				new DateColDef({
					headerName: this.translationService.getTR('ecall-requested-deliverydate-from-grid'),
					field: 'requestedDeliveryDateFrom',
					valueGetter: params => {
						if (!params.data) {
							return undefined;
						}
						const item = <EcallOffCardItemResponse & { requestedDateForCallOffType?: string }> params.data;
						return item.requestedDateForCallOffType === 'ASAP'
							? item.callOffItemCreationTimestamp
							: item.requestedDeliveryDateFrom;
					},
				}),
				new DateColDef({
					headerName: this.translationService.getTR('ecall-requested-deliverydate-to-grid'),
					field: 'requestedDeliveryDateTo',
				}),
				new CommonColDef({
					headerName: this.translationService.getTR('ecall-off.supplier-call-off-reference'),
					field: 'callOffTechnicalReference'
				}),
				new CommonColDef({
					headerName: this.translationService.getTR('ecall-off.ship-to'),
					field: 'shipToDescription'
				}),
				new CommonColDef({
					headerName: this.translationService.getTR('ecall-off.requested-ship-to'),
					field: 'callOffShipToDescription'
				}),
				new DateColDef({
					headerName: this.translationService.getTR('ecall-off-batch-cancellation-date'),
					field: 'batchCancellationTimestamp',
				}),
			]
		});
	}

	private buildReadyToShipOrCalledGrid(batches: Batch[]): CommonGridOptions<Batch> {
		return new CommonGridOptions<Batch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: batches,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<Batch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams
				}),
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('batch.detailed-batch-status'),
					field: 'statusDescription'
				}),
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('batch.date-for-dispatch'),
					field: 'readyToShipDate'
				}),
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('batch.planned-loading-date'),
					field: 'plannedShipmentDate'
				}),
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('batch.current-location'),
					field: 'currentLocationDescription'
				}),
			]
		});
	}

	private getTransportReference(params: ValueGetterParams): string {
		const batch = <OrderBatch> params.data;
		return batch.shipments.length > 0
			? batch.shipments[batch.shipments.length - 1].transportReference
			: '';
	}

	private getTransportModeDescription(params: ValueGetterParams): string {
		const batch = <OrderBatch> params.data;
		return batch.shipments.length > 0
			? batch.shipments[batch.shipments.length - 1].transportModeDescription
			: '';
	}
}
